// Stage 2 (Get Involved Hub) — public-facing CTA bundle rendered on the event
// page, RSVP success page, and (via separate slideshow file) the post-event
// slideshow. Captures four flavours of "I want to get more involved" signal:
//   1. Become an Associate — long-term community helper signup (existing
//      /interest form on community-admin)
//   2. Volunteer at an event — one-shot help-on-the-night signup (same form;
//      different framing/UTM)
//   3. "We want Mindstone in <city>" — inline submission to
//      /api/public/location-suggestions
//   4. "I want to organize Mindstone events" — inline submission to
//      /api/public/organizer-interests
//
// The two inline forms POST directly from the browser to community-admin
// (CORS is `*` globally on CA — see community-admin/server/index.ts), so we
// don't need a web.space-side action or proxy route. Bearer auth is not
// required: the public endpoints are intentionally unauthenticated and rate-
// limited per-IP on the backend.
//
// All outbound clicks carry a consistent UTM scheme so admin can attribute
// where a signup originated:
//   utm_source=mindstone&utm_medium=<source>&utm_campaign=community-amp
//   &utm_content=<cta-id>&ref=<location-name>

import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Textarea,
} from "@mindstonehq/ui";
import {
  Megaphone,
  HandHeart,
  MapPin,
  Users,
  Mic,
  Check,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useConfig } from "~/hooks/webconfig";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import {
  buildInterestUrl,
  type GetInvolvedSource,
} from "~/components/community/get-involved-urls";

// GetInvolvedSource + buildInterestUrl now live in the React-free
// get-involved-urls.ts (Stage 12) so the URL logic is unit-testable. Re-exported
// here for the existing importers that reach for the type via the component file.
export type { GetInvolvedSource };

export interface GetInvolvedHubProps {
  /** Display name for the city ("Paris, France" or "London") — used as the
   *  default `cityName` for the inline forms and in CTA copy. */
  locationName: string;
  /** Optional community-admin location ID (kept in props for parity with the
   *  plan; web.space doesn't currently know it, so it's unused at runtime —
   *  the backend matches by `cityName` instead). */
  locationId?: string | null;
  /** Optional URL-safe slug (likewise unused; reserved for future linking). */
  citySlug?: string | null;
  /** Source identifier for admin inbox triage + analytics. */
  source: GetInvolvedSource;
  /** Optional fade-in delay in ms — used on the RSVP success page where the
   *  confirmation card should appear first and the hub should slide in after. */
  fadeInDelayMs?: number;
  /** Server-resolved distinct-email demand for this city. Drives a gated
   *  social-proof line; rendered only at/above MIN_SOCIAL_PROOF. */
  interestCount?: number;
  /** Social-proof phrasing: "events" (we already run here) or "bring" (a city
   *  we don't run yet). Defaults to "events". */
  demandVariant?: "events" | "bring";
  /** Layout variant. "full" (default) is the original 4-CTA hub used on the
   *  event / rsvp / city routes — unchanged. "compact" (Stage 12) is the trimmed
   *  Get Involved section shown on the public events LISTING: exactly Speak,
   *  Organize, and Become an Associate. Only the compact variant surfaces the
   *  Speak CTA, so the 4 pre-Stage-12 routes keep rendering exactly as before. */
  variant?: "full" | "compact";
}

const MIN_SOCIAL_PROOF = 3;

type FormState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "success" }
  | { status: "error"; message: string };

interface PublicEndpointPayload {
  cityName: string;
  email: string;
  name?: string;
  message?: string;
  source: GetInvolvedSource;
}

// ---------------------------------------------------------------------------
// Submission helper — single source of truth for the cross-origin POST.
// Both inline forms share this; differences are just the endpoint path.
// ---------------------------------------------------------------------------

async function submitToCommunityAdmin(
  baseUrl: string,
  path: "/api/public/location-suggestions" | "/api/public/organizer-interests",
  payload: PublicEndpointPayload,
): Promise<void> {
  // Trim trailing slash from baseUrl to avoid `https://ca//api/...` requests.
  const url = `${baseUrl.replace(/\/$/, "")}${path}`;

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });

  if (res.status === 429) {
    throw new Error("You've sent a few of these recently — please try again later.");
  }
  if (!res.ok) {
    // Best-effort error parsing — fall back to a friendly default.
    // eslint-disable-next-line lingui/no-unlocalized-strings -- caller translates message
    let detail = "Something went wrong. Please try again.";
    try {
      const body = (await res.json()) as { error?: string; issues?: unknown[] };
      if (body?.error === "validation_failed") {
        // eslint-disable-next-line lingui/no-unlocalized-strings -- caller translates message
        detail = "Please check the form and try again.";
      }
    } catch {
      // ignore JSON parse failure; keep default
    }
    throw new Error(detail);
  }
}

// ---------------------------------------------------------------------------
// Inline form for the two "tell us about <X>" submissions. Encapsulates the
// expandable disclosure, the form fields, the submit state, and the success
// confirmation so the parent component stays declarative.
// ---------------------------------------------------------------------------

interface InlineSubmissionFormProps {
  /** Lucide icon component rendered next to the title. */
  Icon: React.ComponentType<{ className?: string }>;
  /** Headline shown on the collapsed row. */
  title: string;
  /** Helper copy beneath the title (kept short — one line). */
  subtitle: string;
  /** "Open" button label. */
  ctaLabel: string;
  /** Copy shown in the green confirmation state after a successful submit. */
  successCopy: string;
  /** Pre-filled value for the cityName field. */
  defaultCityName: string;
  /** Community-admin base URL (from `useConfig().communityUrl`). */
  communityUrl: string;
  /** Endpoint to hit on submit. */
  endpointPath:
    | "/api/public/location-suggestions"
    | "/api/public/organizer-interests";
  /** Source value sent in the payload. */
  source: GetInvolvedSource;
}

function InlineSubmissionForm({
  Icon,
  title,
  subtitle,
  ctaLabel,
  successCopy,
  defaultCityName,
  communityUrl,
  endpointPath,
  source,
}: InlineSubmissionFormProps) {
  const { t } = useLingui();
  const [expanded, setExpanded] = useState(false);
  const [state, setState] = useState<FormState>({ status: "idle" });
  // The cityName is editable so an attendee can submit a suggestion for a
  // different city if they want to (e.g. "I'm at the Paris event but actually
  // want Mindstone in Lyon"). Default seeded from the page's location.
  const [cityName, setCityName] = useState(defaultCityName);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");

  const isSubmitting = state.status === "submitting";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Minimal client-side validation — the backend Zod schema is the source
    // of truth, but we want to avoid round-tripping obvious mistakes.
    if (!cityName.trim() || !email.trim()) return;
    setState({ status: "submitting" });
    try {
      await submitToCommunityAdmin(communityUrl, endpointPath, {
        cityName: cityName.trim(),
        email: email.trim(),
        name: name.trim() ? name.trim() : undefined,
        message: message.trim() ? message.trim() : undefined,
        source,
      });
      setState({ status: "success" });
    } catch (err) {
      setState({
        status: "error",
        message: err instanceof Error ? err.message : t(msg`Something went wrong`),
      });
    }
  }

  // Once successful we keep the disclosure expanded and replace the form
  // with a thank-you message so the user knows the click landed.
  if (state.status === "success") {
    return (
      <div className="rounded-md border border-green-200 bg-green-50 p-4 text-sm dark:border-green-900/40 dark:bg-green-900/20">
        <div className="flex items-start gap-2">
          <Check className="mt-0.5 h-4 w-4 flex-none text-green-600 dark:text-green-400" />
          <div>
            <p className="font-medium text-green-900 dark:text-green-100">
              <Trans>Thanks — we'll be in touch.</Trans>
            </p>
            <p className="mt-1 text-green-800 dark:text-green-200">
              {successCopy}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border bg-background/40">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-3 p-3 text-left"
        aria-expanded={expanded}
      >
        <div className="flex items-start gap-3">
          <Icon className="mt-0.5 h-5 w-5 flex-none text-tertiary" />
          <div>
            <p className="text-sm font-medium">{title}</p>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 flex-none text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 flex-none text-muted-foreground" />
        )}
      </button>

      {expanded && (
        <form onSubmit={handleSubmit} className="space-y-3 p-3 pt-0">
          <div className="space-y-1.5">
            <Label htmlFor={`gih-city-${endpointPath}`}><Trans>City</Trans></Label>
            <Input
              id={`gih-city-${endpointPath}`}
              value={cityName}
              onChange={(e) => setCityName(e.target.value)}
              required
              maxLength={120}
              autoComplete="address-level2"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`gih-email-${endpointPath}`}><Trans>Email</Trans></Label>
            <Input
              id={`gih-email-${endpointPath}`}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              maxLength={255}
              autoComplete="email"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`gih-name-${endpointPath}`}>
              <Trans>Name <span className="text-xs text-muted-foreground">(optional)</span></Trans>
            </Label>
            <Input
              id={`gih-name-${endpointPath}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              autoComplete="name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`gih-msg-${endpointPath}`}>
              <Trans>
                Anything you want to share?{" "}
                <span className="text-xs text-muted-foreground">(optional)</span>
              </Trans>
            </Label>
            <Textarea
              id={`gih-msg-${endpointPath}`}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={2000}
              rows={3}
            />
          </div>

          {state.status === "error" && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-900 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-100">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
              <span>{state.message}</span>
            </div>
          )}

          <Button
            type="submit"
            size="sm"
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? t(msg`Sending…`) : ctaLabel}
          </Button>
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Outbound CTA row — anchor button styled like the inline form's collapsed
// row. Kept symmetric so the four CTAs visually align.
// ---------------------------------------------------------------------------

interface OutboundCTAProps {
  Icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  href: string;
}

function OutboundCTA({ Icon, title, subtitle, href }: OutboundCTAProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-between gap-3 rounded-md border border-border bg-background/40 p-3 transition-colors hover:bg-muted/40"
    >
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-5 w-5 flex-none text-tertiary" />
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      <ChevronUp className="h-4 w-4 flex-none -rotate-90 text-muted-foreground" />
    </a>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function GetInvolvedHub({
  locationName,
  source,
  fadeInDelayMs,
  interestCount,
  demandVariant = "events",
  variant = "full",
}: GetInvolvedHubProps) {
  const { t } = useLingui();
  const config = useConfig();
  // These CTAs/forms live on community-admin (`/interest` + `/api/public/*`),
  // so prefer the explicit admin host when present.
  const communityAdminUrl = config.communityAdminUrl || config.communityUrl;

  // Optional fade-in for the RSVP success page placement. We start the hub
  // hidden and reveal it via inline opacity transition once the timer fires.
  // This avoids relying on a specific tailwind-animate utility being present.
  const [visible, setVisible] = useState(!fadeInDelayMs);
  useEffect(() => {
    if (!fadeInDelayMs) return;
    const t = setTimeout(() => setVisible(true), fadeInDelayMs);
    return () => clearTimeout(t);
  }, [fadeInDelayMs]);

  if (!communityAdminUrl) return null;

  // The `ref` slug is informational only — backend ignores it. It exists so
  // admins can see "where did this signup come from?" in raw CA logs.
  const refSlug = locationName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  const isCompact = variant === "compact";

  // Speak CTA (Stage 12) — outbound to the interest form preselected to the
  // speaker role. Only rendered in the compact listing variant.
  const speakCta = (
    <OutboundCTA
      Icon={Mic}
      title={t(msg`Speak at a Mindstone event`)}
      subtitle={t(msg`Share your expertise with the community.`)}
      href={buildInterestUrl(
        communityAdminUrl,
        source,
        "speaker-signup",
        refSlug,
      )}
    />
  );

  const associateCta = (
    <OutboundCTA
      Icon={Users}
      title={t(msg`Become a Mindstone Associate`)}
      subtitle={t(msg`Help us grow your local community.`)}
      href={buildInterestUrl(
        communityAdminUrl,
        source,
        "associate-signup",
        refSlug,
      )}
    />
  );

  const organizeCta = (
    <InlineSubmissionForm
      Icon={Megaphone}
      title={t(msg`Want to organize Mindstone events?`)}
      subtitle={t(msg`Drive the community in your city.`)}
      ctaLabel={t(msg`Express interest`)}
      successCopy={t(msg`We'll reach out to talk about organising in your city.`)}
      defaultCityName={locationName}
      communityUrl={communityAdminUrl}
      endpointPath="/api/public/organizer-interests"
      source={source}
    />
  );

  return (
    <Card
      className="transition-opacity duration-700"
      style={{ opacity: visible ? 1 : 0 }}
    >
      <CardHeader>
        <CardTitle><Trans>Get involved in {locationName}</Trans></CardTitle>
        <p className="text-sm text-muted-foreground">
          <Trans>Help bring more practical AI events to your city.</Trans>
        </p>
        {typeof interestCount === "number" &&
          interestCount >= MIN_SOCIAL_PROOF && (
            <p className="text-sm font-medium text-foreground">
              {demandVariant === "bring"
                ? t(msg`Join ${interestCount} people asking us to bring Mindstone to ${locationName}.`)
                : t(msg`Join ${interestCount} people who want more Mindstone events in ${locationName}.`)}
            </p>
          )}
      </CardHeader>
      <CardContent className="space-y-2">
        {isCompact ? (
          <>
            {/* Compact listing variant (Stage 12): Speak, Organize, Associate. */}
            {speakCta}
            {organizeCta}
            {associateCta}
          </>
        ) : (
          <>
            {associateCta}
            <OutboundCTA
              Icon={HandHeart}
              title={t(msg`Volunteer at an event`)}
              subtitle={t(msg`Lend a hand on the night — no commitment.`)}
              href={buildInterestUrl(
                communityAdminUrl,
                source,
                "volunteer-signup",
                refSlug,
              )}
            />
            <InlineSubmissionForm
              Icon={MapPin}
              title={t(msg`Know another city that needs this?`)}
              subtitle={t(msg`Tell us where we should host next.`)}
              ctaLabel={t(msg`Submit`)}
              successCopy={t(msg`We'll review and let you know when there's an event nearby.`)}
              defaultCityName={locationName}
              communityUrl={communityAdminUrl}
              endpointPath="/api/public/location-suggestions"
              source={source}
            />
            {organizeCta}
          </>
        )}
      </CardContent>
    </Card>
  );
}
